import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import {
    parseLedgerBasis,
    parsePositiveInteger,
} from "@components/routes/productEntityOpenPaths";
import { useOverlays } from "contexts/OverlayContext";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAccountingTabContext } from "../useAccountingTab";
import { useEntitiesWithAccountingAvailable } from "../useEntitiesWithAccountingAvailable";

const GENERAL_LEDGER_PATH = "/accounting/general-ledger";

export const GeneralLedgerJournalEntryRoute: React.FC = () => {
    const { journalEntryId } = useParams<{ journalEntryId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useStateAwareNavigate();
    const { organizationId, workspaceId, initializeScope } = useScope();
    const { filters, setFilters } = useAccountingTabContext();
    const { openOverlay } = useOverlays();
    const entities = useEntitiesWithAccountingAvailable();
    const openedKeyRef = useRef<string | null>(null);

    const entityId = useMemo(
        () => parsePositiveInteger(searchParams.get("entityId")),
        [searchParams],
    );
    const ledgerBasis = useMemo(
        () => parseLedgerBasis(searchParams.get("ledgerBasis")),
        [searchParams],
    );
    const targetWorkspaceId = searchParams.get("workspaceId");
    const closePath = useMemo(() => {
        const params = new URLSearchParams(searchParams);
        params.delete("workspaceId");
        const queryString = params.toString();
        return queryString
            ? `${GENERAL_LEDGER_PATH}?${queryString}`
            : GENERAL_LEDGER_PATH;
    }, [searchParams]);
    const handleDrawerClose = useCallback(() => {
        navigate(closePath, { replace: true });
    }, [closePath, navigate]);

    useEffect(() => {
        if (!journalEntryId || !entityId) {
            navigate(GENERAL_LEDGER_PATH, { replace: true });
            return;
        }

        if (targetWorkspaceId && targetWorkspaceId !== workspaceId) {
            openedKeyRef.current = null;
            initializeScope({
                organizationId,
                workspaceId: targetWorkspaceId,
                source: OrganizationMemberClientAccessSource.HYDRATE,
            });
            return;
        }

        const entity = entities.find((candidate) => candidate.id === entityId);
        if (!entity) {
            if (entities.length > 0) {
                navigate(GENERAL_LEDGER_PATH, { replace: true });
            }
            return;
        }

        const shouldUpdateFilters = filters.entityId !== entityId;
        if (shouldUpdateFilters) {
            setFilters((current) => ({
                ...current,
                entityId,
            }));
        }

        const key = `${entityId}:${journalEntryId}:${ledgerBasis ?? ""}`;
        if (openedKeyRef.current === key) {
            return;
        }
        openedKeyRef.current = key;
        openOverlay("journalEntryDrawer", {
            entity,
            journalEntryId,
            ledgerBasis,
            onClose: handleDrawerClose,
        });
    }, [
        entities,
        entityId,
        filters.entityId,
        initializeScope,
        journalEntryId,
        ledgerBasis,
        navigate,
        openOverlay,
        organizationId,
        handleDrawerClose,
        setFilters,
        targetWorkspaceId,
        workspaceId,
    ]);

    return null;
};
